import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Form,
  Input,
  Card,
  Typography,
  Tooltip,
  Popconfirm,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { supportTicketAPI } from '../api';
import SupportTicketDialog from './SupportTicketDialog';
import { compressImage, isImageFile, formatFileSize } from '../utils/imageCompression';

const { Title } = Typography;
const { TextArea } = Input;

const MySupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await supportTicketAPI.getAll();
      const sortedTickets = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTickets(sortedTickets);
    } catch (error) {
      message.error('Failed to fetch tickets');
      
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (values) => {
    setCreateLoading(true);
    try {
      // 第一步：创建支持工单（只包含标题）
      const response = await supportTicketAPI.create({ title: values.title });
      
      // 第二步：处理附件（压缩图片）
      const processedFiles = [];
      for (const file of fileList) {
        if (isImageFile(file.originFileObj)) {
          // 图片文件：检查大小并压缩
          if (file.originFileObj.size > 1024 * 1024) { // 超过1MB
            message.loading(`Compressing image ${file.name}...`, 0);
            try {
              const compressedFile = await compressImage(file.originFileObj, 700); // 压缩到700KB
              processedFiles.push(compressedFile);
              message.destroy();
              message.success(`Image ${file.name} compressed (${formatFileSize(file.originFileObj.size)} → ${formatFileSize(compressedFile.size)})`);
            } catch (error) {
              message.destroy();
              message.warning(`Image ${file.name} compression failed, using original file`);
              processedFiles.push(file.originFileObj);
            }
          } else {
            processedFiles.push(file.originFileObj);
          }
        } else {
          // 非图片文件：直接添加
          processedFiles.push(file.originFileObj);
        }
      }
      
      // 第三步：添加第一条消息（包含文本和处理后的附件）
      const formData = new FormData();
      formData.append('content', values.first_comment);
      
      // 添加处理后的附件
      processedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      await supportTicketAPI.addComment(response.data.id, formData);
      
      message.success('Support ticket created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchTickets();
      // 创建成功后立即打开对话框
      setSelectedTicket(response.data);
      setDialogVisible(true);
    } catch (error) {
      message.error('Failed to create support ticket');
      
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    try {
      await supportTicketAPI.delete(ticketId);
      message.success('Support ticket deleted successfully');
      fetchTickets();
    } catch (error) {
      message.error('Failed to delete support ticket');
      
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setDialogVisible(true);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
    setSelectedTicket(null);
    fetchTickets(); // 刷新列表以更新状态
  };

  const columns = [
    {
      title: 'Ticket Title',
      dataIndex: 'title',
      key: 'title',
      width: '30%',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '18%',
      render: (status) => (
        <Tag 
          color={status === 'closed' ? 'default' : 'green'}
          icon={status === 'closed' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
        >
          {status === 'closed' ? 'Closed' : 'Open'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '18%',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Last Activity',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '18%',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <Tooltip title="Delete support ticket">
          <Popconfirm
            title="Delete this support ticket?"
            description="This action cannot be undone and will delete all conversations and attachments."
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteTicket(record.id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>My Support Tickets</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            Create Support Ticket
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={tickets}
          loading={loading}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleViewTicket(record),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} tickets`,
          }}
          size="middle"
        />
      </Card>

      {/* Create Support Ticket Modal */}
      <Modal
        title="Create Support Ticket"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={createLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTicket}
        >
          <Form.Item
            name="title"
            label="Ticket Title"
            rules={[
              { required: true, message: 'Please enter ticket title' },
              { min: 2, message: 'Ticket title must be at least 2 characters' },
              { max: 80, message: 'Ticket title cannot exceed 80 characters' },
            ]}
          >
            <Input placeholder="Enter a brief title for your ticket" />
          </Form.Item>
          <Form.Item
            name="first_comment"
            label="Message"
            rules={[
              { required: true, message: 'Please describe your issue' },
              { min: 10, message: 'Message must be at least 10 characters' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe your request or issue. If it is related to a specific order or product, please also include the related order numbers and product links."
            />
          </Form.Item>
          <Form.Item
            label="Attachments"
          >
            <Upload
              beforeUpload={(file) => {
                const allowedTypes = [
                  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.ms-powerpoint',
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                  'application/vnd.ms-excel',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ];

                if (!allowedTypes.includes(file.type)) {
                  message.error('File type not supported. Please upload images, PDF, DOC, DOCX, PPT, PPTX, XLS, or XLSX files.');
                  return false;
                }

                // 图片文件限制1MB（会自动压缩），其他文件限制3MB
                const isImage = file.type.startsWith('image/');
                const maxSize = isImage ? 1024 * 1024 : 3 * 1024 * 1024; // 1MB for images, 3MB for others
                const sizeLabel = isImage ? '1MB' : '3MB';
                
                if (file.size > maxSize) {
                  if (isImage) {
                    message.warning(`Image ${file.name} exceeds 1MB, will be automatically compressed before upload`);
                    // 图片文件允许添加，因为会自动压缩
                  } else {
                    message.error(`File size cannot exceed ${sizeLabel}`);
                    return Upload.LIST_IGNORE; // 阻止文件添加到列表
                  }
                }

                return false; // Don't auto upload
              }}
              fileList={fileList}
              onChange={({ fileList: newFileList }) => {
                setFileList(newFileList);
              }}
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
            >
              <Button icon={<PaperClipOutlined />}>
                Select Files
              </Button>
            </Upload>
            <br />
            <p>Supported file types: images and documents (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX)<br />File size cannot exceed 3MB for documents.</p>
          </Form.Item>
        </Form>
      </Modal>

      {/* Support Ticket Dialog */}
      {dialogVisible && selectedTicket && (
        <SupportTicketDialog
          ticket={selectedTicket}
          visible={dialogVisible}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};

export default MySupportTickets;
